import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as FileUploadIcon } from 'assets/icons/fileUpload.svg';
import {
  TASK_ATTACHMENT_ALLOWED_TYPES,
  TASK_ATTACHMENT_ALLOWED_TYPES_STRING,
  TASK_ATTACHMENT_MAX_FILE_SIZE,
  TASK_TYPE_ACTION,
} from 'constants/general';

import type IFileUpload from 'store/slices/tasksManagement/interfaces/IFileUpload';
import type IFileUploadComponent from './interfaces/IFileUpload';

import styles from './scss/FileUpload.module.scss';

function FileUpload({
  attachmentsFiles,
  onAttachmentsChange,
  onFileUpload,
  onFileSizeError,
  label,
  placeholder,
  accept = TASK_ATTACHMENT_ALLOWED_TYPES_STRING,
  allowedTypes = TASK_ATTACHMENT_ALLOWED_TYPES,
  multiple = true,
  maxFileSize = TASK_ATTACHMENT_MAX_FILE_SIZE,
  accessDeleteFile = true,
  userOwnerId,
  createTypeAction,
  onDeleteFileAttachment,
}: IFileUploadComponent) {
  const { t } = useTranslation();
  const [isDragActive, setIsDragActive] = useState(false);

  const validateFileSize = useCallback((file: File): boolean => {
    if (file.size > maxFileSize) {
      onFileSizeError?.(file.name, maxFileSize);
      return false;
    }
    return true;
  }, [maxFileSize, onFileSizeError]);

  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files) {
      const fileArray = Array.from(files);
      const validFiles = fileArray.filter(validateFileSize);

      validFiles.forEach((file) => {
        onFileUpload?.(file);
      });
    }
  }, [onFileUpload, validateFileSize]);

  const handleRemoveFile = useCallback((index: number) => {
    const newAttachments = attachmentsFiles.filter((_: IFileUpload, i: number) => i !== index);
    onAttachmentsChange(newAttachments);
    if (onDeleteFileAttachment) {
      onDeleteFileAttachment(attachmentsFiles[index].id || 0);
    }
  }, [attachmentsFiles, onAttachmentsChange, onDeleteFileAttachment]);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);

    const { files } = event.dataTransfer;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      const filteredFiles = fileArray.filter((file) => {
        const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
        return allowedTypes.includes(fileExtension) && validateFileSize(file);
      });

      if (filteredFiles.length > 0) {
        filteredFiles.forEach((file) => {
          onFileUpload?.(file);
        });
      }
    }
  }, [onFileUpload, allowedTypes, validateFileSize]);

  useEffect(() => {
    const handleWindowDragOver = (event: DragEvent) => {
      event.preventDefault();
    };

    const handleWindowDrop = (event: DragEvent) => {
      event.preventDefault();
    };

    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, []);

  return (
    <div className={styles.fileUpload}>
      <div className={styles.fileUploadHeader}>
        <span className={styles.fileUploadLabel}>
          {label || t('taskManagement.fields.attachments')}
        </span>
      </div>
      <div
        className={`${styles.fileUploadContainer} ${isDragActive ? styles.dragActive : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleFileInputChange}
          className={styles.fileInput}
          id="file-upload"
        />
        <label htmlFor="file-upload" className={styles.fileUploadButton}>
          <span className={styles.fileUploadIcon}>
            <FileUploadIcon />
          </span>
          <span className={styles.fileUploadText}>
            {placeholder || t('taskManagement.fields.attachFile')}
          </span>
        </label>
      </div>
      {attachmentsFiles.length > 0 && (
        <div className={styles.filesList}>
          {attachmentsFiles.map((file: IFileUpload, index: number) => (
            <div key={`${file.fileName}-${file.fileSize}`} className={styles.fileItem}>
              <FileUploadIcon />
              <span className={styles.fileName}>{file.fileName}</span>
              {accessDeleteFile
              || (attachmentsFiles[index]?.uploadedById === userOwnerId
                || createTypeAction === TASK_TYPE_ACTION.CREATE) ? (
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className={styles.removeFileButton}
                  >
                    <CloseIcon />
                  </button>
                ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FileUpload;
